import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/i18n";

function updateConsent(granted) {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: granted ? "granted" : "denied",
      ad_storage: granted ? "granted" : "denied",
      ad_user_data: granted ? "granted" : "denied",
      ad_personalization: granted ? "granted" : "denied",
    });
    if (granted) window.gtag("event", "page_view");
  }
}

export function CookieConsent() {
  const { t, lp } = useI18n();
  const [show, setShow] = useState(false);
  const [view, setView] = useState("banner"); // banner | settings
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("ns_consent");
    if (saved === "granted") {
      updateConsent(true);
    } else if (!saved) {
      setShow(true);
    }
    const reopen = () => { setView("banner"); setShow(true); };
    window.addEventListener("ns:open-consent", reopen);
    return () => window.removeEventListener("ns:open-consent", reopen);
  }, []);

  const decide = (granted) => {
    localStorage.setItem("ns_consent", granted ? "granted" : "denied");
    updateConsent(granted);
    setShow(false);
  };

  if (typeof window !== "undefined" && (window.location.pathname.split("/").includes("embed") || window.location.pathname.includes("embed-ranking"))) return null;
  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center" data-testid="cookie-banner">
      <div className="absolute inset-0 z-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setView("banner")} />
      <div className="relative z-10 m-4 w-full max-w-lg overflow-hidden rounded-3xl border border-border bg-card shadow-2xl">
        <div className="flex items-center gap-3 border-b border-border px-6 pb-4 pt-6">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Cookie className="h-5 w-5" />
          </span>
          <h2 className="font-head text-lg font-700 text-foreground" data-testid="cookie-title">
            {view === "settings" ? t("common.consentManageTitle") : t("common.consentTitle")}
          </h2>
        </div>

        {view === "banner" ? (
          <div className="px-6 py-5">
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("common.consentText")}{" "}
              <Link to={lp("/privacy")} onClick={() => setShow(false)} className="font-600 text-primary underline underline-offset-2 hover:opacity-80" data-testid="cookie-privacy-link">
                {t("common.consentPrivacy")}
              </Link>
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
              <button data-testid="cookie-accept" onClick={() => decide(true)} className="flex-1 rounded-full bg-primary px-5 py-2.5 text-sm font-600 text-primary-foreground transition-transform hover:-translate-y-0.5">
                {t("common.consentAcceptAll")}
              </button>
              <button data-testid="cookie-reject" onClick={() => decide(false)} className="flex-1 rounded-full border border-border px-5 py-2.5 text-sm font-600 text-foreground transition-colors hover:bg-accent">
                {t("common.consentRejectAll")}
              </button>
              <button data-testid="cookie-settings" onClick={() => setView("settings")} className="flex-1 rounded-full px-5 py-2.5 text-sm font-600 text-muted-foreground transition-colors hover:bg-accent">
                {t("common.consentSettings")}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5">
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-2xl border border-border bg-secondary/50 p-4">
                <Shield className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-700 text-foreground">{t("common.consentNecessary")}</span>
                    <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-600 text-emerald-700">{t("common.consentAlwaysOn")}</span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t("common.consentNecessaryDesc")}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-2xl border border-border p-4">
                <BarChart3 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-700 text-foreground">{t("common.consentAnalytics")}</span>
                    <Switch checked={analytics} onCheckedChange={setAnalytics} data-testid="cookie-toggle-analytics" />
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t("common.consentAnalyticsDesc")}</p>
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse">
              <button data-testid="cookie-save" onClick={() => decide(analytics)} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-600 text-primary-foreground transition-transform hover:-translate-y-0.5">
                <Check className="h-4 w-4" /> {t("common.consentSave")}
              </button>
              <button data-testid="cookie-back" onClick={() => setView("banner")} className="flex-1 rounded-full border border-border px-5 py-2.5 text-sm font-600 text-foreground transition-colors hover:bg-accent">
                {t("common.consentBack")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
